import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard, History, Copy, CheckCircle, Download, Timer, AlertCircle, RefreshCw, Globe } from 'lucide-react'
import type { Transaction, BankConfig } from '../../../lib/types'
import { useAuth } from '../../auth/hooks/useAuth'
import AuthModal from '../../auth/components/AuthModal'
import { apiGetJson, apiPostJson, apiPostJsonRaw } from '../../../lib/apiClient'
import { subscribe } from '../../realtime/userEventsSocket'
import { unwrapFirst, unwrapList } from '../../../lib/apiUnwrap'
import { motion } from 'framer-motion'
import { scaleUp, slideInBottom, staggerContainer } from '../../../lib/animations'

const PRESET_AMOUNTS = [50000, 100000, 200000, 500000, 1000000, 2000000]

type DepositBonusTier = {
  minDeposit: number
  bonusPercent: number
}

type DepositBonusPreview = {
  depositAmount: number
  bonusAmount: number
  creditedAmount: number
  bonusPercent: number
  minDeposit: number
  reason: string
}

const DEFAULT_DEPOSIT_BONUS_TIERS: DepositBonusTier[] = [
  { minDeposit: 100_000, bonusPercent: 2 },
  { minDeposit: 500_000, bonusPercent: 3 },
  { minDeposit: 1_000_000, bonusPercent: 4 },
  { minDeposit: 2_000_000, bonusPercent: 5 },
]

function toPositiveNumber(input: unknown): number {
  const n = Number(input)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.floor(n)
}

function normalizeDepositBonusTiers(raw: unknown): DepositBonusTier[] {
  if (!Array.isArray(raw)) return DEFAULT_DEPOSIT_BONUS_TIERS

  const normalized = raw
    .map((item: any) => {
      const minDeposit = toPositiveNumber(item?.min_deposit ?? item?.minDeposit)
      const bonusPercent = toPositiveNumber(item?.bonus_percent ?? item?.bonusPercent)
      if (minDeposit <= 0 || bonusPercent <= 0) return null
      return { minDeposit, bonusPercent: Math.min(100, bonusPercent) } as DepositBonusTier
    })
    .filter((x): x is DepositBonusTier => Boolean(x))
    .sort((a, b) => a.minDeposit - b.minDeposit)

  return normalized.length > 0 ? normalized : DEFAULT_DEPOSIT_BONUS_TIERS
}

function buildDepositBonusPreview(amount: number, tiers: DepositBonusTier[]): DepositBonusPreview {
  const depositAmount = Math.max(0, Math.floor(amount || 0))
  const sorted = [...tiers].sort((a, b) => a.minDeposit - b.minDeposit)
  const applied = sorted.reduce<DepositBonusTier | null>((acc, tier) => {
    if (depositAmount >= tier.minDeposit) return tier
    return acc
  }, null)

  const bonusPercent = applied?.bonusPercent || 0
  const minDeposit = applied?.minDeposit || 0
  const bonusAmount = bonusPercent > 0 ? Math.floor((depositAmount * bonusPercent) / 100) : 0
  const creditedAmount = depositAmount + bonusAmount

  const reason = bonusPercent > 0 && minDeposit > 0
    ? `Nạp từ ${minDeposit.toLocaleString('vi-VN')}đ nên được thưởng ${bonusPercent}%.`
    : 'Chưa đạt mốc thưởng nạp (từ 100.000đ).'

  return { depositAmount, bonusAmount, creditedAmount, bonusPercent, minDeposit, reason }
}

export default function TopUpPage() {
  const { user, loading: authLoading, isInitializing } = useAuth()
  const navigate = useNavigate()
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [customAmount, setCustomAmount] = useState('')
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [pendingAmount, setPendingAmount] = useState<number | null>(null)
  const [transferContent, setTransferContent] = useState('')
  const [timeLeft, setTimeLeft] = useState(15 * 60) // 15 minutes
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [activeBankConfig, setActiveBankConfig] = useState<BankConfig | null>(null)
  const [activeTransaction, setActiveTransaction] = useState<Transaction | null>(null)
  const [depositBonusTiers, setDepositBonusTiers] = useState<DepositBonusTier[]>(DEFAULT_DEPOSIT_BONUS_TIERS)
  const [lastTopUpBreakdown, setLastTopUpBreakdown] = useState<DepositBonusPreview | null>(null)
  const [currentTime, setCurrentTime] = useState(Date.now())
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const lastProcessedTxId = useRef<string | null>(null)
  const successModalTimerRef = useRef<NodeJS.Timeout | null>(null)
  const activeTransactionRef = useRef<Transaction | null>(null)

  useEffect(() => {
    activeTransactionRef.current = activeTransaction
  }, [activeTransaction])

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 1000)
    return () => {
      clearInterval(timer)
      if (successModalTimerRef.current) {
        clearTimeout(successModalTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const amountParam = params.get('amount')
    if (amountParam) {
      const amount = parseInt(amountParam)
      if (!isNaN(amount) && amount > 0) {
        setCustomAmount(amount.toString())
        setSelectedAmount(null)
      }
    }
  }, [])

  const selectedInputAmount = useMemo(() => {
    const fromCustom = customAmount ? parseInt(customAmount.replace(/[^0-9]/g, ''), 10) : 0
    return selectedAmount || (Number.isFinite(fromCustom) ? fromCustom : 0)
  }, [selectedAmount, customAmount])

  const selectedAmountPreview = useMemo(
    () => buildDepositBonusPreview(selectedInputAmount, depositBonusTiers),
    [selectedInputAmount, depositBonusTiers]
  )

  const activeQrPreview = useMemo(() => {
    const depositAmount = toPositiveNumber(pendingAmount) || toPositiveNumber(activeTransaction?.amount)
    const bonusFromMetadata = toPositiveNumber((activeTransaction as any)?.metadata?.deposit_bonus_amount)
    const creditedFromMetadata = toPositiveNumber((activeTransaction as any)?.metadata?.credited_amount)
    const fallback = buildDepositBonusPreview(depositAmount, depositBonusTiers)

    const bonusAmount = bonusFromMetadata > 0 ? bonusFromMetadata : fallback.bonusAmount
    const creditedAmount = creditedFromMetadata > 0 ? creditedFromMetadata : (depositAmount + bonusAmount)
    const bonusPercentFromMetadata = toPositiveNumber((activeTransaction as any)?.metadata?.deposit_bonus_percent)
    const minDepositFromMetadata = toPositiveNumber((activeTransaction as any)?.metadata?.deposit_bonus_min_deposit)
    const reasonFromMetadata = String((activeTransaction as any)?.metadata?.deposit_bonus_reason || '').trim()

    return {
      depositAmount,
      bonusAmount,
      creditedAmount,
      bonusPercent: bonusPercentFromMetadata > 0 ? bonusPercentFromMetadata : fallback.bonusPercent,
      minDeposit: minDepositFromMetadata > 0 ? minDepositFromMetadata : fallback.minDeposit,
      reason: reasonFromMetadata || fallback.reason,
    }
  }, [pendingAmount, activeTransaction, depositBonusTiers])

  const isExpired = (createdAt: string) => {
    if (!createdAt) return false
    const created = new Date(createdAt).getTime()
    if (!Number.isFinite(created)) return false
    const now = currentTime
    return now - created > 15 * 60 * 1000
  }

  const getRemainingSeconds = useCallback((createdAt: string) => {
    if (!createdAt) return 15 * 60
    const created = new Date(createdAt).getTime()
    if (!Number.isFinite(created)) return 15 * 60
    const now = currentTime
    const diff = 15 * 60 * 1000 - (now - created)
    return Math.max(0, Math.floor(diff / 1000))
  }, [currentTime])

  useEffect(() => {
    if (!activeTransaction) return
    const remaining = getRemainingSeconds(activeTransaction.created_at)
    if (remaining <= 0) {
      setQrUrl(null)
      setActiveTransaction(null)
    } else {
      setTimeLeft(remaining)
    }
  }, [activeTransaction, getRemainingSeconds])

  const fetchActiveBankConfig = useCallback(async () => {
    const fallbackConfig: BankConfig = {
      id: 'fallback-hardcoded',
      bank_name: 'TPBank',
      bank_code: '970423',
      account_name: 'LUONG QUOC THAI',
      account_number: '60394352614',
      qr_template: 'compact',
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    try {
      const resp = await apiGetJson<any>('/api/bank-configs/active')
      const data = unwrapFirst<BankConfig>(resp)
      if (data) {
        setActiveBankConfig(data)
      } else {
        // Fallback explicitly on frontend
        setActiveBankConfig(fallbackConfig)
      }
    } catch (error) {
      console.error('Error fetching bank config using fallback:', error)
      setActiveBankConfig(fallbackConfig)
    }
  }, [])

  const fetchDepositBonusConfig = useCallback(async () => {
    try {
      const resp = await apiGetJson<any>('/api/global-settings/deposit_bonus_config')
      const setting = unwrapFirst<{ value?: string | null }>(resp)
      const rawValue = setting?.value

      if (!rawValue) {
        setDepositBonusTiers(DEFAULT_DEPOSIT_BONUS_TIERS)
        return
      }

      let parsed: unknown = rawValue
      try {
        parsed = JSON.parse(rawValue)
      } catch {
        // keep raw value and fall back to defaults below
      }

      setDepositBonusTiers(normalizeDepositBonusTiers(parsed))
    } catch (error) {
      console.error('Error fetching deposit bonus config:', error)
      setDepositBonusTiers(DEFAULT_DEPOSIT_BONUS_TIERS)
    }
  }, [])

  const userId = user?.id

  const fetchTransactions = useCallback(async () => {
    if (!userId) return

    try {
      const resp = await apiGetJson<any>('/api/transactions?type=top_up&limit=50')
      const list = unwrapList<Transaction>(resp)
      setTransactions(list)
      return list
    } catch (error) {
      console.error('Error fetching transactions:', error)
      return []
    }
  }, [userId])

  useEffect(() => {
    if (isInitializing || authLoading) return
    if (!userId) return

    fetchActiveBankConfig()
    fetchDepositBonusConfig()
    fetchTransactions()
    return
  }, [isInitializing, authLoading, userId, fetchActiveBankConfig, fetchDepositBonusConfig, fetchTransactions])

  useEffect(() => {
    if (isInitializing || authLoading) return
    if (!userId) return

    const unsub = subscribe('balance_updated', (data) => {
      const txId = String(data?.transaction_id || '')
      const currentActive = activeTransactionRef.current
      if (!currentActive) return
      if (!txId || txId !== currentActive.id) return

      if (lastProcessedTxId.current === currentActive.id) return
      lastProcessedTxId.current = currentActive.id

      setQrUrl(null)
      setActiveTransaction(null)
      setPendingAmount(null)

      const topupAmount = toPositiveNumber(data?.topup_amount)
      const bonusAmount = toPositiveNumber(data?.bonus_amount)
      const creditedAmount = toPositiveNumber(data?.amount) || (topupAmount + bonusAmount)
      const fallbackPreview = buildDepositBonusPreview(topupAmount, depositBonusTiers)
      const bonusPercentFromEvent = toPositiveNumber(data?.bonus_percent)
      const bonusMinDepositFromEvent = toPositiveNumber(data?.bonus_min_deposit)
      const reasonFromEvent = typeof data?.bonus_reason === 'string' ? data.bonus_reason.trim() : ''

      const bonusPercent = bonusPercentFromEvent > 0
        ? bonusPercentFromEvent
        : (topupAmount > 0 && bonusAmount > 0
          ? Math.floor((bonusAmount * 100) / topupAmount)
          : fallbackPreview.bonusPercent)

      setLastTopUpBreakdown({
        depositAmount: topupAmount,
        bonusAmount,
        creditedAmount: creditedAmount > 0 ? creditedAmount : (topupAmount + bonusAmount),
        bonusPercent,
        minDeposit: bonusMinDepositFromEvent > 0 ? bonusMinDepositFromEvent : fallbackPreview.minDeposit,
        reason: reasonFromEvent || fallbackPreview.reason,
      })

      setShowSuccessModal(true)
      if (successModalTimerRef.current) {
        clearTimeout(successModalTimerRef.current)
      }
      successModalTimerRef.current = setTimeout(() => {
        setShowSuccessModal(false)
      }, 5000)

      void fetchTransactions()
    })

    return () => {
      unsub()
    }
  }, [isInitializing, authLoading, userId, fetchTransactions, depositBonusTiers])

  useEffect(() => {
    if (isInitializing || authLoading) return
    setShowAuthModal(!user)
  }, [isInitializing, authLoading, user])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }



  const buildQrUrl = (amount: number, content: string) => {
    const bankCode = activeBankConfig?.bank_code || '970423'
    const accNum = activeBankConfig?.account_number || '60394352614'
    const accName = activeBankConfig?.account_name || 'LUONG QUOC THAI'
    return `https://img.vietqr.io/image/${bankCode}-${accNum}-compact2.png?amount=${amount}&addInfo=${encodeURIComponent(content)}&accountName=${encodeURIComponent(accName)}`
  }

  const resumeTransaction = (transaction: Transaction) => {
    if (transaction.status !== 'pending' || isExpired(transaction.created_at)) return

    const content = String((transaction as any)?.metadata?.transfer_content || '')
    const apiQrUrl = String((transaction as any)?.metadata?.qr_url || '')
    setQrUrl(apiQrUrl || buildQrUrl(transaction.amount, content))
    setPendingAmount(transaction.amount)
    setTransferContent(content)
    setActiveTransaction(transaction)
  }

  const handleCancelTransaction = async (e: React.MouseEvent, transactionId: string) => {
    e.stopPropagation() // Prevent triggering resumeTransaction
    if (!confirm('Bạn có chắc chắn muốn hủy giao dịch này không?')) return

    try {
      await apiPostJson<any>('/api/transactions/cancel', { transactionId })

      if (activeTransaction?.id === transactionId) {
        setQrUrl(null)
        setActiveTransaction(null)
        setPendingAmount(null)
      }

      fetchTransactions()
    } catch (error) {
      console.error('Error canceling transaction:', error)
      alert('Có lỗi xảy ra khi hủy giao dịch')
    }
  }

  const handleTopUp = async (amount: number) => {
    if (!user) {
      setShowAuthModal(true)
      return
    }

    if (amount < 20000) {
      alert('Số tiền nạp tối thiểu là 20,000đ')
      return
    }

    // Check limit: max 2 pending transactions
    const pendingTransactions = transactions.filter(t => t.status === 'pending' && !isExpired(t.created_at))
    if (pendingTransactions.length >= 2) {
      alert('Bạn đang có 2 giao dịch đang xử lý. Vui lòng hoàn tất hoặc đợi chúng hết hạn.')
      return
    }

    setLastTopUpBreakdown(null)
    setLoading(true)
    try {
      const resp = await apiPostJsonRaw<any>('/api/transactions/top-up/create', { amount })
      const transaction = unwrapFirst<Transaction>(resp) || undefined
      if (!transaction?.id) throw new Error('Không tạo được giao dịch')

      const content = String((transaction as any)?.metadata?.transfer_content || '')

      const apiQrUrl = String((transaction as any)?.metadata?.qr_url || '')
      const qrCodeUrl = apiQrUrl || buildQrUrl(amount, content)

      const normalizedTx: Transaction = {
        ...(transaction as any),
        created_at: (transaction as any)?.created_at || new Date().toISOString(),
        metadata: {
          ...((transaction as any)?.metadata || {}),
          transfer_content: content,
          qr_url: qrCodeUrl,
        },
      }

      setTransferContent(content)
      setQrUrl(qrCodeUrl)
      setPendingAmount(amount)
      setActiveTransaction(normalizedTx)

      fetchTransactions()
    } catch (error: any) {
      console.error('Error topping up:', error)
      const msg = error?.message || 'Có lỗi xảy ra khi tạo giao dịch nạp tiền'
      alert(msg)
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    })
  }

  const getStatusBadge = (transaction: Transaction) => {
    if (transaction.status === 'pending' && isExpired(transaction.created_at)) {
      return (
        <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-gray-100 text-gray-400">
          Hết hạn
        </span>
      )
    }

    switch (transaction.status) {
      case 'completed':
        return (
          <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-green-100 text-green-600">
            Thành công
          </span>
        )
      case 'pending': {
        const seconds = getRemainingSeconds(transaction.created_at)
        return (
          <div className="flex flex-col items-end gap-1">
            <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-600">
              Đang xử lý
            </span>
            {seconds > 0 && (
              <span className="text-[10px] text-amber-500 font-medium">
                Còn {formatTime(seconds)}
              </span>
            )}
          </div>
        )
      }
      case 'failed':
        return (
          <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-red-100 text-red-600">
            Thất bại
          </span>
        )
      default:
        return null
    }
  }

  if (!user) {
    return (
      <>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Nạp Tiền</h1>
            <p className="text-gray-600">Vui lòng đăng nhập để sử dụng tính năng này</p>
          </div>
        </div>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">Nạp Tiền</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Nạp tiền vào ví để mua sắm các sản phẩm số</p>
        </div>
        <div className="bg-white dark:bg-gray-900 px-6 py-3 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex items-center space-x-4">
          <div className="bg-amber-50 p-2 rounded-xl">
            <CreditCard className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-300 font-medium uppercase tracking-wider">Số dư hiện tại</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{user.balance.toLocaleString('vi-VN')}đ</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Step 1: Input Amount */}
        <div className={`${qrUrl ? 'lg:col-span-12' : 'lg:col-span-8'} space-y-8`}>
          {!qrUrl ? (
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl shadow-amber-50 dark:shadow-none border border-gray-100 dark:border-gray-800 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-8">
                  <div className="bg-amber-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">1</div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Nhập số tiền muốn nạp</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Chọn mệnh giá nhanh</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {PRESET_AMOUNTS.map((amount) => (
                        <motion.button
                          key={amount}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedAmount(amount)
                            setCustomAmount('')
                          }}
                          className={`py-4 rounded-2xl border-2 transition-all duration-200 font-bold ${selectedAmount === amount
                            ? 'border-amber-600 bg-amber-50 text-amber-700 shadow-md transform -translate-y-1 dark:bg-amber-950/30 dark:text-amber-200'
                            : 'border-gray-100 hover:border-amber-200 hover:bg-gray-50 text-gray-600 dark:border-gray-800 dark:hover:border-amber-800 dark:hover:bg-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {amount.toLocaleString('vi-VN')}đ
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Hoặc nhập số tiền tùy chọn</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={customAmount}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '')
                          setCustomAmount(val)
                          setSelectedAmount(null)
                        }}
                        placeholder="VD: 100,000"
                        className="w-full pl-6 pr-16 py-5 bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-amber-500 focus:bg-white dark:focus:bg-gray-900 rounded-2xl text-2xl font-bold text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-all outline-none"
                      />
                      <span className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 font-bold text-xl">đ</span>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Số tiền tối thiểu: 20,000đ
                    </p>
                  </div>

                  <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-950/20 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-bold text-emerald-800 dark:text-emerald-200">Thưởng nạp tự động</h3>
                      <span className="text-[11px] text-emerald-700 dark:text-emerald-300 font-medium">Cộng trực tiếp vào ví</span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {depositBonusTiers.map((tier) => {
                        const active = selectedInputAmount > 0 && selectedInputAmount >= tier.minDeposit
                        return (
                          <div
                            key={`${tier.minDeposit}-${tier.bonusPercent}`}
                            className={`rounded-xl border px-2.5 py-2 text-center ${active
                              ? 'border-emerald-300 bg-white dark:bg-emerald-900/30'
                              : 'border-emerald-100 dark:border-emerald-900/40 bg-white/80 dark:bg-gray-900/50'
                              }`}
                          >
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">Từ</div>
                            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                              {tier.minDeposit.toLocaleString('vi-VN')}đ
                            </div>
                            <div className="text-sm font-extrabold text-emerald-600 dark:text-emerald-300">
                              +{tier.bonusPercent}%
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    {selectedInputAmount > 0 ? (
                      <div className="mt-3 rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-white dark:bg-gray-900 p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 dark:text-gray-300">Tiền nạp</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {selectedAmountPreview.depositAmount.toLocaleString('vi-VN')}đ
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-gray-600 dark:text-gray-300">Thưởng nạp</span>
                          <span className="font-semibold text-emerald-600 dark:text-emerald-300">
                            +{selectedAmountPreview.bonusAmount.toLocaleString('vi-VN')}đ
                            {selectedAmountPreview.bonusPercent > 0 ? ` (${selectedAmountPreview.bonusPercent}%)` : ''}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1 pt-1 border-t border-emerald-100 dark:border-emerald-900/40">
                          <span className="font-semibold text-gray-700 dark:text-gray-200">Tổng cộng vào ví</span>
                          <span className="font-extrabold text-emerald-700 dark:text-emerald-200">
                            {selectedAmountPreview.creditedAmount.toLocaleString('vi-VN')}đ
                          </span>
                        </div>
                        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">
                          Lý do: {selectedAmountPreview.reason}
                        </p>
                      </div>
                    ) : (
                      <p className="mt-3 text-xs text-emerald-700 dark:text-emerald-300">
                        Nhập số tiền để xem trước mức thưởng được cộng vào ví.
                      </p>
                    )}
                  </div>

                  {activeBankConfig && (
                    <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20 rounded-2xl border border-amber-100 dark:border-amber-900/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white dark:bg-gray-900 rounded-xl flex items-center justify-center shadow-sm border border-amber-100 dark:border-amber-900/50">
                          <Globe className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Ngân hàng thụ hưởng</p>
                          <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{activeBankConfig.bank_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Số tài khoản</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{activeBankConfig.account_number}</p>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      if (selectedInputAmount > 0) handleTopUp(selectedInputAmount)
                    }}
                    disabled={loading || selectedInputAmount <= 0 || !activeBankConfig}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-5 rounded-2xl font-bold text-xl shadow-lg shadow-amber-200 transition-all disabled:opacity-50 disabled:shadow-none flex items-center justify-center group"
                  >
                    {loading ? (
                      <RefreshCw className="h-6 w-6" />
                    ) : (
                      <>
                        <span>Tiếp tục</span>
                        <RefreshCw className="h-5 w-5 ml-2 group-hover:rotate-180 transition-transform duration-500" />
                      </>
                    )}
                  </button>
                  {!activeBankConfig && (
                    <div className="mt-3 p-3 bg-red-50 text-red-600 rounded-xl text-sm font-medium text-center flex items-center justify-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Hệ thống nạp tiền đang bảo trì
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left: QR Display */}
              <motion.div {...scaleUp(0.2)} className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 text-center flex flex-col items-center">
                <div className="mb-6">
                  <span className="px-4 py-2 bg-red-50 text-red-600 rounded-full text-sm font-bold flex items-center mx-auto w-fit">
                    <Timer className="h-4 w-4 mr-2" />
                    Đơn hàng hết hạn sau: {formatTime(timeLeft)}
                  </span>
                </div>

                <h3 className="text-2xl font-black text-gray-900 dark:text-gray-100 mb-6 uppercase tracking-tight">Quét mã QR để thanh toán</h3>

                <div className="relative p-6 bg-white dark:bg-gray-950 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-3xl mb-6 group">
                  <img src={qrUrl} alt="QR Code" className="w-[300px] h-[300px] rounded-lg" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 dark:bg-gray-950/80 rounded-3xl backdrop-blur-sm">
                    <button
                      onClick={() => window.open(qrUrl, '_blank')}
                      className="bg-amber-600 text-white p-4 rounded-2xl shadow-xl flex items-center gap-2 font-bold"
                    >
                      <Download className="h-5 w-5" />
                      Tải mã QR
                    </button>
                  </div>
                </div>

                <div className="w-full space-y-3">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-800">
                    <span className="text-gray-500 dark:text-gray-400 font-medium">Tiền nạp:</span>
                    <span className="text-xl font-black text-gray-900 dark:text-gray-100">
                      {activeQrPreview.depositAmount.toLocaleString('vi-VN')}đ
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl border border-emerald-100 dark:border-emerald-900/40">
                    <span className="text-emerald-700 dark:text-emerald-300 font-medium">Thưởng nạp:</span>
                    <span className="text-xl font-black text-emerald-600 dark:text-emerald-300">
                      +{activeQrPreview.bonusAmount.toLocaleString('vi-VN')}đ
                      {activeQrPreview.bonusPercent > 0 ? ` (${activeQrPreview.bonusPercent}%)` : ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-100 dark:border-amber-900/40">
                    <span className="text-amber-700 dark:text-amber-300 font-medium">Tổng cộng vào ví:</span>
                    <span className="text-2xl font-black text-amber-600">
                      {activeQrPreview.creditedAmount.toLocaleString('vi-VN')}đ
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 px-1">Lý do: {activeQrPreview.reason}</p>
                  <button
                    onClick={() => {
                      setQrUrl(null)
                      setActiveTransaction(null)
                    }}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 font-medium text-sm py-2 transition-colors"
                  >
                    ← Hủy và tạo đơn khác
                  </button>

                  <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Nếu bạn đã nạp xong?</p>
                    <button
                      onClick={() => navigate('/profile#topups')}
                      className="text-amber-600 hover:text-amber-700 font-bold text-sm hover:underline"
                    >
                      Bấm vào đây để xem lịch sử nạp
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Right: Payment Info */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 p-8">
                  <h3 className="text-xl font-bold flex items-center mb-6">
                    <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
                    <span className="text-gray-900 dark:text-gray-100">Thông tin chuyển khoản SePay</span>
                  </h3>

                  <div className="space-y-4">
                    {[
                      {
                        label: 'Ngân hàng',
                        value: activeTransaction?.metadata?.bank_info?.bank_name || 'TPBank',
                        key: 'bank'
                      },
                      {
                        label: 'Số tài khoản',
                        value: activeTransaction?.metadata?.bank_info?.account_number || '60394352614',
                        key: 'account'
                      },
                      {
                        label: 'Chủ tài khoản',
                        value: activeTransaction?.metadata?.bank_info?.account_holder || 'LUONG QUOC THAI',
                        key: 'name'
                      },
                      { label: 'Nội dung chuyển khoản', value: transferContent, key: 'content', highlight: true },
                    ].map((item) => (
                      <div key={item.key} className={`p-4 rounded-2xl border-2 transition-all ${item.highlight ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-transparent hover:border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase mb-1">{item.label}</p>
                            <p className={`font-bold ${item.highlight ? 'text-lg text-yellow-800 dark:text-yellow-200' : 'text-gray-900 dark:text-gray-100'}`}>{item.value}</p>
                          </div>
                          {item.key !== 'bank' && item.key !== 'name' && (
                            <button
                              onClick={() => copyToClipboard(item.value, item.key)}
                              className={`p-2 rounded-xl transition-all ${copiedField === item.key ? 'bg-green-500 text-white' : 'bg-white dark:bg-gray-950 text-gray-400 hover:text-amber-600 shadow-sm'}`}
                            >
                              {copiedField === item.key ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 p-6 bg-green-50 dark:bg-green-950/20 rounded-2xl border border-green-100 dark:border-green-900/50">
                    <h4 className="flex items-center text-green-800 dark:text-green-200 font-bold mb-2 text-sm">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Hệ thống tự động cộng tiền
                    </h4>
                    <p className="text-green-700 dark:text-green-300 text-xs leading-relaxed">
                      Sau khi chuyển khoản thành công, hệ thống SePay sẽ tự động xác nhận và cộng tiền vào tài khoản của bạn trong vòng 1-2 phút. Vui lòng giữ đúng nội dung chuyển khoản để được xử lý tự động.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Transaction History Sidebar */}
        {!qrUrl && (
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
              <div className="p-6 border-b border-gray-50 dark:border-gray-800 flex items-center justify-between">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 flex items-center">
                  <History className="h-5 w-5 mr-2 text-amber-600" />
                  Giao dịch gần đây
                </h3>
                <button
                  type="button"
                  onClick={() => fetchTransactions()}
                  className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  title="Làm mới"
                  aria-label="Làm mới"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {transactions.length === 0 ? (
                  <div className="p-12 text-center text-gray-400 dark:text-gray-500">
                    <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-10" />
                    <p className="text-sm font-medium">Chưa có giao dịch</p>
                  </div>
                ) : (
                  <motion.div className="divide-y divide-gray-50 dark:divide-gray-800" variants={staggerContainer(0.06)} initial="initial" whileInView="animate" viewport={{ once: true, margin: '-30px' }}>
                    {transactions.map((transaction, index) => {
                      const canResume = transaction.status === 'pending' && !isExpired(transaction.created_at)
                      const topupAmount = toPositiveNumber(transaction.amount)
                      const bonusAmount = toPositiveNumber((transaction as any)?.metadata?.deposit_bonus_amount)
                      const creditedAmount = toPositiveNumber((transaction as any)?.metadata?.credited_amount) || (topupAmount + bonusAmount)
                      const displayAmount = transaction.status === 'completed'
                        ? (creditedAmount > 0 ? creditedAmount : topupAmount)
                        : topupAmount
                      return (
                        <motion.div
                          key={transaction.id}
                          {...slideInBottom(index * 0.03)}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: '-30px' }}
                          transition={{ duration: 0.3, delay: index * 0.03 }}
                          whileHover={{ y: -2 }}
                          className={`p-4 transition-colors relative group/item ${canResume ? 'hover:bg-amber-50 dark:hover:bg-amber-950/20 cursor-pointer border-l-4 border-l-transparent hover:border-l-amber-500' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                          onClick={() => canResume && resumeTransaction(transaction)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-bold text-gray-900 dark:text-gray-100">+{displayAmount.toLocaleString('vi-VN')}đ</span>
                            <div className="flex items-center gap-2">
                              {canResume && (
                                <button
                                  onClick={(e) => handleCancelTransaction(e, transaction.id)}
                                  className="opacity-0 group-hover/item:opacity-100 p-1 hover:bg-slate-100 text-slate-700 rounded-md transition-all text-[10px] font-bold flex items-center gap-1 border border-slate-300 dark:hover:bg-slate-800 dark:text-slate-100 dark:border-slate-600"
                                  title="Hủy giao dịch"
                                >
                                  Hủy đơn
                                </button>
                              )}
                              {getStatusBadge(transaction)}
                            </div>
                          </div>
                          {bonusAmount > 0 && (
                            <div className="text-[11px] text-emerald-600 dark:text-emerald-300 mb-1">
                              {transaction.status === 'completed' ? 'Đã cộng' : 'Dự kiến'}: gốc {topupAmount.toLocaleString('vi-VN')}đ + thưởng {bonusAmount.toLocaleString('vi-VN')}đ
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 dark:text-gray-500">{new Date(transaction.created_at).toLocaleDateString('vi-VN')}</span>
                            <div className="flex items-center gap-2">
                              {canResume && (
                                <span className="text-[10px] text-amber-600 font-bold">Bấm để tiếp tục</span>
                              )}
                              <span className="text-[10px] text-gray-300 dark:text-gray-500 font-mono">#{transaction.id.slice(-6).toUpperCase()}</span>
                            </div>
                          </div>
                        </motion.div>
                      )
                    })}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full my-auto h-[min(30rem,calc(100vh-2rem))] overflow-y-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Thanh toán thành công!</h3>
              <p className="text-gray-600 mb-6">Số dư của bạn đã được cập nhật</p>
              {lastTopUpBreakdown && (
                <div className="bg-emerald-50 rounded-xl p-4 mb-4 text-left border border-emerald-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Tiền nạp</span>
                    <span className="font-semibold text-gray-900">
                      {lastTopUpBreakdown.depositAmount.toLocaleString('vi-VN')}đ
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-600">Thưởng nạp</span>
                    <span className="font-semibold text-emerald-700">
                      +{lastTopUpBreakdown.bonusAmount.toLocaleString('vi-VN')}đ
                      {lastTopUpBreakdown.bonusPercent > 0 ? ` (${lastTopUpBreakdown.bonusPercent}%)` : ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-emerald-200">
                    <span className="font-semibold text-gray-700">Tổng cộng vào ví</span>
                    <span className="font-extrabold text-emerald-700">
                      {lastTopUpBreakdown.creditedAmount.toLocaleString('vi-VN')}đ
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-emerald-700">Lý do: {lastTopUpBreakdown.reason}</p>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-500 mb-1">Số dư hiện tại</p>
                <p className="text-3xl font-bold text-amber-600">{user?.balance.toLocaleString('vi-VN')}đ</p>
              </div>
              {/* <button
                onClick={() => {
                  setShowSuccessModal(false)
                  if (successModalTimerRef.current) {
                    clearTimeout(successModalTimerRef.current)
                  }
                }}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-xl font-bold transition-colors"
              >
                Đóng
              </button> */}
              <p className="text-xs text-gray-400 mt-3">Tự động đóng sau 5 giây</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
